import { useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import { GET_MATCH_WEEK_TOP_PLAYERS } from '../../../graphql/queries/matchWeekTopPlayers';
import type {
  GetMatchWeekTopPlayersQuery,
  GetMatchWeekTopPlayersQueryVariables,
  Player,
} from '../../../types/graphql-generated';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LineupPitch } from '../../fantasy-leagues-tabs/components/lineup-editor/LineupPitch';
import { TacticsSelector } from '../../fantasy-leagues-tabs/components/lineup-editor/TacticsSelector';
import { PlayerSelectionModal } from '../../fantasy-leagues-tabs/team/components/PlayerSelectionModal';
import { LineupService, ROLE_LABELS, type LineupSlot } from '../../fantasy-leagues-tabs/services/LineupService';

const DEFAULT_TACTICS = '1-3-4-3';

interface WeekPlayer {
  id: string;
  player: Player;
  weekFantasyScore: number;
}

type WeekLineupSlot = LineupSlot<WeekPlayer>;

const emptyLineup = (): WeekLineupSlot[] =>
  Array.from({ length: 11 }, (_, i) => ({ position: i + 1, fantasyTeamPlayerId: null, fantasyTeamPlayer: null }));

/**
 * "11 de la jornada": lets global admins pick the best lineup of a match week
 * among its best scoring players, to take a screenshot for social media. Nothing
 * is saved.
 */
export const TeamOfTheWeekPage = () => {
  const matchWeekId = useParams<'matchWeekId'>().matchWeekId!;
  const [tactics, setTactics] = useState(DEFAULT_TACTICS);
  const [lineup, setLineup] = useState<WeekLineupSlot[]>(emptyLineup);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);

  const { data, loading, error } = useQuery<GetMatchWeekTopPlayersQuery, GetMatchWeekTopPlayersQueryVariables>(
    GET_MATCH_WEEK_TOP_PLAYERS,
    { variables: { matchWeekId } }
  );
  const matchWeekTopPlayers = data?.matchWeekTopPlayers;
  const weekPlayers: WeekPlayer[] = (matchWeekTopPlayers?.players ?? []).map(({ player, fantasyScore }) => ({
    id: player.id,
    player: player as Player,
    weekFantasyScore: fantasyScore,
  }));

  const handleTacticsChange = (newTactics: string) => {
    setTactics(newTactics);
    setLineup(LineupService.updateLineupFromTactic(lineup, newTactics));
  };

  const handlePlayerSelect = (weekPlayer: WeekPlayer | null) => {
    if (selectedPosition === null) return;

    setLineup(
      lineup.map((slot) =>
        slot.position === selectedPosition
          ? {
              position: selectedPosition,
              fantasyTeamPlayerId: weekPlayer?.id ?? null,
              fantasyTeamPlayer: weekPlayer,
              fantasyScore: weekPlayer?.weekFantasyScore,
            }
          : slot
      )
    );
    setSelectedPosition(null);
  };

  const selectedPlayerIds = new Set(
    lineup
      .filter((slot) => slot.fantasyTeamPlayer && slot.position !== selectedPosition)
      .map((slot) => slot.fantasyTeamPlayer!.id)
  );
  const requiredRole = selectedPosition ? LineupService.getRoleForPosition(selectedPosition, tactics) : '';

  return (
    <IonPage>
      <AppSectionHeader variant="admin">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin/jornadas" text="" aria-label="Volver" />
          </IonButtons>
          <IonTitle className="ml-4">
            {matchWeekTopPlayers ? `11 de la jornada ${matchWeekTopPlayers.matchWeek.weekNumber}` : '11 de la jornada'}
          </IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        {loading && (
          <div className="flex min-h-[200px] items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar los jugadores de la jornada</p>
          </IonText>
        )}

        {!loading && !error && data && !matchWeekTopPlayers && (
          <IonText color="medium">
            <p className="text-center">No se ha encontrado la jornada</p>
          </IonText>
        )}

        {matchWeekTopPlayers && (
          <div className="space-y-4">
            <div className="mx-auto w-auto max-w-xs text-center">
              <TacticsSelector tactics={tactics} onChange={handleTacticsChange} />
            </div>

            <LineupPitch lineup={lineup} tactics={tactics} onSlotClick={setSelectedPosition} />

            {selectedPosition && (
              <PlayerSelectionModal
                isOpen
                onClose={() => setSelectedPosition(null)}
                onSelect={handlePlayerSelect}
                requiredRole={requiredRole}
                requiredRoleLabel={ROLE_LABELS[requiredRole]}
                availablePlayers={weekPlayers.filter((weekPlayer) => !selectedPlayerIds.has(weekPlayer.id))}
                currentSelection={lineup[selectedPosition - 1]?.fantasyTeamPlayer ?? null}
                weekNumber={matchWeekTopPlayers.matchWeek.weekNumber}
              />
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};
